// fallow-ignore-filed
/**
 * Click Tracking Wrapper Function
 * Simple wrapper to track clicks on elements
 */

import type { MouseEvent } from 'react';
import { clickCounter } from '../services/EventTracker';

/**
 * Wraps a click handler to track clicks
 *
 * @param elementKey - The key to track clicks under (e.g., 'settings_button', 'nav_tab')
 * @param originalHandler - The original click handler function
 * @param metadata - Optional metadata to store with the click
 * @returns Wrapped click handler that tracks clicks
 *
 * @example
 * const handleClick = trackClick('settings_button', () => {
 *   setShowModal(true);
 * });
 *
 * @example
 * const handleClick = trackClick('nav_tab', () => {
 *   selectCategory(categoryName);
 * }, { category: 'SLOTS' });
 */
export function trackClick<T = MouseEvent>(
    elementKey: string,
    originalHandler?: (event: T) => void,
    metadata?: Record<string, unknown>
): (event: T) => void {
    return (event: T) => {
        // Track the click with metadata
        clickCounter.trackEvent(elementKey, metadata);

        // Call original handler if provided
        if (originalHandler) {
            originalHandler(event);
        }
    };
}

/**
 * Alternative: Get tracking props for JSX elements
 * Returns an object with onClick handler that can be spread on elements
 *
 * @param elementKey - The key to track clicks under
 * @param originalHandler - Optional original click handler
 * @param metadata - Optional metadata to store with the click
 * @returns Props object to spread on element
 *
 * @example
 * <button {...getClickTrackingProps('settings_button', handleOriginalClick)}>
 *   Settings
 * </button>
 *
 * @example
 * <button {...getClickTrackingProps('nav_tab', handleTabClick, { category: 'SLOTS' })}>
 *   Slots
 * </button>
 */
// fallow-ignore-next-line unused-export
export function getClickTrackingProps<T = MouseEvent>(
    elementKey: string,
    originalHandler?: (event: T) => void,
    metadata?: Record<string, unknown>
) {
    return {
        onClick: trackClick(elementKey, originalHandler, metadata)
    };
}

/**
 * For elements that already have className-based identification
 * Extracts element key from className and tracks clicks
 *
 * @param event - The click event
 * @param classNameToKey - Function to convert className to tracking key
 *
 * @example
 * // If you have elements with className="game-card-slots"
 * const handleClick = (event) => {
 *   trackClickByClassName(event, (className) => className.replace('-', '_'));
 *   // ... rest of logic
 * };
 */
// fallow-ignore-next-line unused-export
export function trackClickByClassName(
    event: MouseEvent,
    classNameToKey: (className: string) => string = (className) => className
): void {
    const element = event.currentTarget as HTMLElement;
    const className = element.className;

    if (className) {
        // Extract first class name or use a custom mapping
        const firstClass = className.split(' ')[0];
        const elementKey = classNameToKey(firstClass);
        clickCounter.trackEvent(elementKey);
    }
}

/**
 * For elements with data attributes
 * Tracks clicks using data-sentry-id or similar attributes
 *
 * @param event - The click event
 * @param dataAttribute - The data attribute name (default: 'sentry-id')
 *
 * @example
 * <button data-sentry-id="settings_button" onClick={handleClick}>
 *   Settings
 * </button>
 */
// fallow-ignore-next-line unused-export
export function trackClickByDataAttribute(
    event: MouseEvent,
    dataAttribute: string = 'sentry-id'
): void {
    const element = event.currentTarget as HTMLElement;
    const elementKey = element.dataset[dataAttribute];

    if (elementKey) {
        clickCounter.trackEvent(elementKey);
    }
}
